import React from 'react';
import { emergentSystems, emergentRule, emergentContext } from '../data/mock';
import StatsStrip from './StatsStrip';
import { Card, Label, Marked } from './primitives';

const SystemCard = ({ system }) => (
  <Card tint={system.tint} className="p-5 sm:p-7 h-full flex flex-col">
    <Label className="mb-3 text-ink/60">{system.kicker}</Label>
    <h3 className="text-xl sm:text-2xl font-bold text-ink tracking-tight leading-snug">
      {system.title}
    </h3>
    <p className="mt-3 text-sm sm:text-[15px] text-ink-soft leading-relaxed">
      <Marked text={system.body} />
    </p>
    {system.points?.length > 0 && (
      <ul className="mt-4 pt-4 border-t border-ink/15 space-y-2">
        {system.points.map((point) => (
          <li key={point} className="flex gap-2.5 text-sm text-ink-soft leading-relaxed">
            <span className="text-flag select-none mt-[1px]">✦</span>
            <span>
              <Marked text={point} />
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const Emergent = () => {
  const { signature, pipeline } = emergentSystems;

  return (
    <section id="emergent" className="bg-cream py-16 sm:py-24 scroll-mt-20">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-marker border-[1.5px] border-ink rounded-full shadow-hard-sm text-[11px] sm:text-xs font-bold uppercase tracking-label text-ink">
          <span className="w-1.5 h-1.5 rounded-full bg-flag" aria-hidden="true" />
          Emergent · Apr - Nov 2026
        </span>
        <h2 className="mt-5 text-3xl sm:text-5xl font-bold text-ink tracking-[-0.03em] leading-[1.08]">
          What I built at <span className="mark">Emergent</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-ink-soft leading-relaxed max-w-2xl">
          {emergentContext}
        </p>

        {/* The numbers */}
        <div className="mt-8 sm:mt-10">
          <StatsStrip />
        </div>

        <div className="mt-12 sm:mt-14">
          <h3 className="text-xl sm:text-2xl font-bold text-ink tracking-[-0.02em]">
            No gut calls. Pure data signals.
          </h3>
          <p className="mt-2 text-sm sm:text-base text-ink-soft leading-relaxed max-w-2xl">
            Three systems that turned creator marketing from taste into evidence.
          </p>
        </div>

        {/* The two signature systems, most prominent */}
        <div className="mt-6 sm:mt-7 grid md:grid-cols-2 gap-5 sm:gap-6">
          {signature.map((system) => (
            <SystemCard key={system.title} system={system} />
          ))}
        </div>

        <div className="mt-5 sm:mt-6 grid md:grid-cols-[1fr_1.15fr] gap-5 sm:gap-6">
          {/* Pipeline */}
          <Card tint="paper" className="p-5 sm:p-6">
            <Label className="mb-2.5">{pipeline.kicker}</Label>
            <h3 className="text-lg font-bold text-ink tracking-tight">{pipeline.title}</h3>
            <p className="mt-2.5 text-sm text-ink-soft leading-relaxed">{pipeline.body}</p>
            <p className="mt-4 font-mono text-[11px] sm:text-xs text-ink leading-relaxed break-words">
              creator vetting → payment → reconciliation
            </p>
          </Card>

          {/* The rule, as a before / after */}
          <Card tint="cream" className="p-5 sm:p-6">
            <Label className="mb-3">The rule</Label>
            <p className="text-sm sm:text-[15px] text-subtle line-through decoration-flag decoration-2">
              “{emergentRule.before}”
            </p>
            <p className="my-1.5 text-flag font-bold" aria-hidden="true">
              ↓
            </p>
            <p className="text-base sm:text-lg font-semibold text-ink leading-snug">
              <span className="mark">“{emergentRule.after}”</span>
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Emergent;
